import type { Stripe } from "@stripe/stripe-js";
// `pure`: the package's main entry injects Stripe.js the moment it is
// imported, on every page that loads this module; this one waits for
// `loadStripe`, which the dashboard calls straight away below.
import { loadStripe } from "@stripe/stripe-js/pure";

import { isMarketingDocument } from "@/routing/marketingDocument";

// r-1970: js.stripe.com can be unreachable for a shopper — most often a
// proxy/VPN that blocks it — and loadStripe then rejects. We reload ONCE to
// recover from a transient failure, but must never loop: a persistent block
// would otherwise refresh the page forever (the customer's "dashboard keeps
// refreshing"). The guard lives in sessionStorage so it survives the reload
// and resets when the tab closes.
const RELOAD_GUARD_KEY = "stripeLoadReloadAttempted";

function hasReloadedForStripe(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_GUARD_KEY) === "1";
  } catch {
    return false;
  }
}

function setReloadedForStripe(value: boolean) {
  try {
    if (value) {
      sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
    } else {
      sessionStorage.removeItem(RELOAD_GUARD_KEY);
    }
  } catch {
    // sessionStorage unavailable (private mode / blocked). We simply lose the
    // dedupe and fall back to a single best-effort reload attempt.
  }
}

type LoadStatus = "pending" | "loaded" | "failed";
let status: LoadStatus = "pending";

/**
 * Failed to load Stripe.js
 * Due to an issue in Stripe, the user will be stuck. https://github.com/stripe/stripe-js/issues/26#issuecomment-1477716731
 * Until this fix is merged https://github.com/stripe/stripe-js/pull/518 we reload
 * the page — but only once (see the guard above). If it still fails, we resolve
 * null so payment surfaces can warn the shopper instead of looping forever.
 */
async function attemptLoad(reloadOnFailure: boolean): Promise<Stripe | null> {
  status = "pending";
  try {
    const stripe = await loadStripe(process.env.NEXT_PUBLIC_STRIPE_KEY!);
    // Recovered — clear the guard so a future transient failure can reload again.
    setReloadedForStripe(false);
    status = "loaded";
    return stripe;
  } catch (err) {
    console.error(err);
    status = "failed";
    if (reloadOnFailure && !hasReloadedForStripe()) {
      setReloadedForStripe(true);
      window.location.reload();
    }
    return null;
  }
}

/**
 * A dashboard document starts loading Stripe.js as soon as this module loads,
 * so it is ready by the time a payment surface asks. A marketing document
 * loads this module too, in the bundle every page shares, but takes no
 * payment: it leaves Stripe alone until something asks for it, as the
 * published marketing site never loaded it, and so never falls into the
 * reload above either.
 */
let stripePromise: Promise<Stripe | null> | null = isMarketingDocument()
  ? null
  : attemptLoad(true);

const getStripe = () => {
  stripePromise ??= attemptLoad(true);
  return stripePromise;
};

/**
 * r-1956: re-attempt loading Stripe.js after a failed load, without reloading
 * the page. Stripe.js clears its own cache on failure, so a new attempt injects
 * the script again — when the shopper has since disconnected the proxy that
 * blocked js.stripe.com, this resolves to a real Stripe instance and every
 * later `getStripe()` call shares it. A pending or successful load is returned
 * as-is, so callers can poll this freely.
 */
export function retryLoadStripe(): Promise<Stripe | null> {
  if (status === "failed") {
    stripePromise = attemptLoad(false);
  }
  return getStripe();
}

export default getStripe;
